import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react'; // Using an icon for the button

const UniversityCard = ({ image, universityName, details, visitUrl }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <motion.div className="university-card" variants={cardVariants}>
      <motion.div className="university-image" variants={itemVariants}>
        <img src={image} alt={universityName} />
      </motion.div>
      <div className="university-details">
        <motion.h3 variants={itemVariants}>{universityName}</motion.h3>
        {Object.entries(details).map(([key, value]) => (
          <motion.p key={key} variants={itemVariants}>
            <strong>{key}:</strong> {value}
          </motion.p>
        ))}
        {visitUrl && (
          <motion.a
            href={visitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="visit-button"
            variants={itemVariants}
            whileHover="hover"
          >
            <span>Visit Now</span>
            <motion.div variants={{ hover: { x: 5 } }}>
              <ArrowRight size={20} />
            </motion.div>
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

const ActivityDetails = () => {
  const [universities, setUniversities] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetch('https://ethiccraft.org/backend/college_chapters/read.php')
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
            setUniversities(data);
            if (data.length > 0) setActiveId(data[0].id);
        } else {
            console.error('API did not return an array:', data);
            setUniversities([]);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch universities:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      y: -20,
      transition: { duration: 0.3, ease: 'easeIn' },
    },
  };

  if (loading) {
    return <div className="loading-state">Loading Chapters...</div>;
  }

  if (!universities.length) {
    return <div className="loading-state">No Chapters Found.</div>;
  }

  const activeUniversity = universities.find((u) => u.id === activeId);
  const activeTitle = activeUniversity ? activeUniversity.title : "Select a Chapter";

  return (
    <div className="page-wrapper">
      <header className="page-header">
        <h1 className="colleges-title">College Chapters</h1>
      </header>
      
      {/* Mobile Navigation Dropdown */}
      <div className="mobile-nav-header">
        <button onClick={() => setMobileNavOpen(!isMobileNavOpen)}>
          {activeTitle}
          <motion.span animate={{ rotate: isMobileNavOpen ? 180 : 0 }}>▼</motion.span>
        </button>
      </div>

      <div className="colleges-container">
        {/* Sidebar navigation */}
        <aside className={`sidebar ${isMobileNavOpen ? 'mobile-open' : ''}`}>
          <nav>
            {universities.map(({ id, title }) => (
              <motion.a
                key={id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`sidebar-item ${activeId === id ? 'active' : ''}`}
                onClick={() => {
                  setActiveId(id);
                  setMobileNavOpen(false); // Close nav on selection
                }}
              >
                {title}
                {activeId === id && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    transition={{ duration: 0.5, type: 'spring', bounce: 0.3 }}
                  />
                )}
              </motion.a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeId}
              variants={sectionVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="content-inner"
            >
              {activeUniversity && (
                <>
                  <h2 className="content-title">{activeUniversity.title}</h2>
                  <UniversityCard
                    image={`https://ethiccraft.org/${activeUniversity.image}`}
                    universityName={activeUniversity.universityName}
                    details={JSON.parse(activeUniversity.details)}
                    visitUrl={activeUniversity.visit_url}
                  />
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ActivityDetails;